/**
 * VideoPostViewer Component
 * 
 * Complete video viewing experience featuring:
 * - Video post metadata display
 * - Integrated VideoPlayer with custom controls
 * - Live CaptionsWidget synchronized with playback
 * - Side-by-side layout: player on left, captions on right
 * - Responsive design that stacks on mobile
 * 
 * Used in: VideoPostDetail page
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    Chip,
    Paper
} from '@mui/material';
import { AccessTime, Person } from '@mui/icons-material';
import VideoPlayer from './VideoPlayer';
import TranscriptWidget from './TranscriptWidget';
import TappedWordsWidget from '../Learning/TappedWordsWidget';
import { userRequest } from '../../requestMethods';

const VideoPostViewer = ({ post, transcripts: legacyTranscripts = [] }) => {
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0); // eslint-disable-line no-unused-vars
    const [videoPlayerRef, setVideoPlayerRef] = useState(null);
    
    // New state for smart transcript fetching
    const [allFetchedTranscripts, setAllFetchedTranscripts] = useState([]);
    const [fetchedTimeRanges, setFetchedTimeRanges] = useState([]);
    const [isFetchingTranscripts, setIsFetchingTranscripts] = useState(false);
    
    // State for tapped words
    const [tappedWords, setTappedWords] = useState([]);
    const [isVideoHidden, setIsVideoHidden] = useState(false);
    const [videoPlayerHeight, setVideoPlayerHeight] = useState(null);
    const videoPlayerContainerRef = useRef(null);

    const handleTimeUpdate = (time) => {
        setCurrentTime(time);
        
        // Check if we need to fetch new transcripts
        // Only fetch if current time is not covered by any fetched time range
        if (post && post._id && !isFetchingTranscripts) {
            const isTimeCovered = fetchedTimeRanges.some(range => 
                time >= range.start && time <= range.end
            );
            
            if (!isTimeCovered) {
                fetchFormattedTranscripts(time);
            }
        }
    };

    const handleDurationChange = (dur) => {
        setDuration(dur);
    };

    // Handle seeking to specific time
    const handleSeekTo = (time) => {
        if (videoPlayerRef && videoPlayerRef.seekTo) {
            videoPlayerRef.seekTo(time);
        }
    };

    // Handle word displayed from interactive text
    const handleWordDisplayed = (word) => {
        // Only add words that have translation or grammar link
        if ((word.t && word.t.trim() !== '') || // word.translation
            (word.l && word.l.trim() !== '')) { // word.link
            
            setTappedWords(prevWords => {
                // Check if word already exists (avoid duplicates)
                const wordKey = word.w;
                const exists = prevWords.some(w => 
                    w.w === wordKey
                );
                
                if (!exists) {
                    // Add new word to the beginning of the array
                    return [word, ...prevWords];
                }
                return prevWords;
            });
        }
    };

    // Handle video visibility toggle
    const handleVideoVisibilityToggle = (hidden) => {
        setIsVideoHidden(hidden);
    };

    // Initial fetch when component mounts or post changes
    useEffect(() => {
        if (!post || !post._id) return;

        // Reset state for new video
        setAllFetchedTranscripts([]);
        setFetchedTimeRanges([]);
        setIsFetchingTranscripts(false);
        
        // Smart transcript fetching function (defined inside useEffect to avoid dependency issues)
        const fetchFormattedTranscripts = async (timestamp) => {
            if (isFetchingTranscripts) return;

            setIsFetchingTranscripts(true);
            
            try {
                const response = await userRequest.get(`/text/formatted/byVideoPost/${post._id}`, {
                    params: { timestamp }
                });

                if (response.data && response.data.transcripts && response.data.textTimeRange) {
                    const newTranscripts = response.data.transcripts;
                    const timeRange = response.data.textTimeRange;
                    
                    // Accumulate new transcripts with existing ones
                    setAllFetchedTranscripts(prev => {
                        // Filter out any duplicates by transcriptId to avoid duplicating transcripts
                        const existingIds = new Set(prev.map(t => t.transcriptId));
                        const uniqueNewTranscripts = newTranscripts.filter(t => !existingIds.has(t.transcriptId));
                        return [...prev, ...uniqueNewTranscripts];
                    });
                    
                    // Add the fetched time range to our tracking
                    setFetchedTimeRanges(prev => [...prev, {
                        start: timeRange.start,
                        end: timeRange.end,
                        textId: timeRange.textId
                    }]);

                    console.log(`✓ Fetched ${newTranscripts.length} transcripts for t=${timestamp}s, covers range ${timeRange.start}s-${timeRange.end}s`);
                } else {
                    console.log(`⚠ No transcripts found for timestamp: ${timestamp}`);
                }
            } catch (error) {
                console.error('Error fetching formatted transcripts:', error);
                // On error, don't add any time range tracking
                // Client will retry on next time update
            } finally {
                setIsFetchingTranscripts(false);
            }
        };
        
        // Fetch initial transcripts for timestamp 0
        fetchFormattedTranscripts(0);
            // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [post?._id]); // Only depend on post._id to avoid infinite loops

    // Smart transcript fetching function for time updates
    const fetchFormattedTranscripts = useCallback(async (timestamp) => {
        if (!post || !post._id || isFetchingTranscripts) return;

        setIsFetchingTranscripts(true);
        
        try {
            const response = await userRequest.get(`/text/formatted/byVideoPost/${post._id}`, {
                params: { timestamp }
            });

            if (response.data && response.data.transcripts && response.data.textTimeRange) {
                const newTranscripts = response.data.transcripts;
                const timeRange = response.data.textTimeRange;
                
                // Accumulate new transcripts with existing ones
                setAllFetchedTranscripts(prev => {
                    // Filter out any duplicates by transcriptId to avoid duplicating transcripts
                    const existingIds = new Set(prev.map(t => t.transcriptId));
                    const uniqueNewTranscripts = newTranscripts.filter(t => !existingIds.has(t.transcriptId));
                    return [...prev, ...uniqueNewTranscripts];
                });
                
                // Add the fetched time range to our tracking
                setFetchedTimeRanges(prev => [...prev, {
                    start: timeRange.start,
                    end: timeRange.end,
                    textId: timeRange.textId
                }]);

                console.log(`🔄 Fetched ${newTranscripts.length} more transcripts for t=${timestamp}s, covers range ${timeRange.start}s-${timeRange.end}s`);
            } else {
                console.log(`⚠ No more transcripts for timestamp: ${timestamp}`);
            }
        } catch (error) {
            console.error('Error fetching formatted transcripts:', error);
            // On error, don't add any time range tracking
            // Client will retry on next time update
        } finally {
            setIsFetchingTranscripts(false);
        }
    }, [post, isFetchingTranscripts]);

    const formatDuration = (minutes) => {
        if (!minutes) return '';
        return `${minutes} min`;
    };

    // Measure video player height to match transcript widget
    useEffect(() => {
        const updateHeight = () => {
            if (videoPlayerContainerRef.current) {
                const height = videoPlayerContainerRef.current.offsetHeight;
                setVideoPlayerHeight(height);
            }
        };

        updateHeight();
        window.addEventListener('resize', updateHeight);
        
        // Use ResizeObserver for more accurate measurements
        let resizeObserver;
        if (videoPlayerContainerRef.current && window.ResizeObserver) {
            resizeObserver = new ResizeObserver(updateHeight);
            resizeObserver.observe(videoPlayerContainerRef.current);
        }

        return () => {
            window.removeEventListener('resize', updateHeight);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
        };
    }, [allFetchedTranscripts.length]); // Re-measure when transcripts load

    // Detect if this is a YouTube Shorts video
    const isShorts = typeof post?.link === 'string' && post.link.includes('/shorts/');

    if (!post) {
        return (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                    Video post not found
                </Typography>
            </Paper>
        );
    }

    return (
        <Box>
            {/* Video Post Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" component="h1" sx={{ mb: 2, fontWeight: 600 }}>
                    {post.title}
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
                    {post.author && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                                {post.author}
                            </Typography>
                        </Box>
                    )}

                    {post.estimatedWatchTime && (
                        <Chip
                            icon={<AccessTime sx={{ fontSize: 16 }} />}
                            label={formatDuration(post.estimatedWatchTime)}
                            size="small"
                            variant="outlined"
                        />
                    )}

                    {post.tags?.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {post.tags.map((tag, index) => (
                                <Chip
                                    key={index}
                                    label={tag}
                                    size="small"
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Tapped Words Widget for Videos */}
            {/* <TappedWordsWidget 
                tappedWords={tappedWords}
                variant="inline"
            /> */}

            {/* Video Player and Transcript Layout */}
            <Box sx={{ 
                display: 'flex', 
                gap: 3,
                flexDirection: { xs: 'column', lg: 'row' },
                alignItems: { xs: 'stretch', lg: 'flex-start' }
            }}>
                {/* Left Column: Video Player + New Words */}
                <Box sx={{ 
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2
                }}>
                    {/* Video Player Container - used as reference for transcript height */}
                    <Box ref={videoPlayerContainerRef} sx={{ position: 'relative' }}>
                        <VideoPlayer
                            videoUrl={post.link}
                            onTimeUpdate={handleTimeUpdate}
                            onDurationChange={handleDurationChange}
                            onPlay={() => console.log('Video started playing')}
                            onPause={() => console.log('Video paused')}
                            ref={setVideoPlayerRef}
                            isShorts={isShorts}
                            transcripts={allFetchedTranscripts}
                            onSeekTo={handleSeekTo}
                            onWordDisplayed={handleWordDisplayed}
                            onVideoVisibilityToggle={handleVideoVisibilityToggle}
                        />
                    </Box>

                    {/* New Words Widget - Below Video Player */}
                    <TappedWordsWidget 
                        tappedWords={tappedWords}
                        variant="inline"
                    />
                </Box>

                {/* Right Column: Transcript Widget - Matches video player height */}
                <Box 
                    id="transcript-container"
                    sx={{ 
                        width: { xs: '100%', lg: '400px' },
                        flexShrink: 0,
                        display: { xs: 'none', lg: 'flex' },
                        flexDirection: 'column',
                        alignSelf: 'flex-start',
                        ...(videoPlayerHeight && {
                            height: `${videoPlayerHeight}px`,
                            maxHeight: `${videoPlayerHeight}px`
                        })
                    }}
                >
                    <TranscriptWidget
                        currentTime={currentTime}
                        transcripts={allFetchedTranscripts}
                        onSeekTo={handleSeekTo}
                        isShorts={isShorts}
                        onWordDisplayed={handleWordDisplayed}
                    />
                </Box>
            </Box>

            {/* Mobile: Transcript Widget Below Video */}
            <Box sx={{ display: { xs: 'block', lg: 'none' }, mt: 3 }}>
                <TranscriptWidget
                    currentTime={currentTime}
                    transcripts={allFetchedTranscripts}
                    onSeekTo={handleSeekTo}
                    isShorts={isShorts}
                    onWordDisplayed={handleWordDisplayed}
                />
            </Box>
        </Box>
    );
};

export default VideoPostViewer; 